import type {MessageInitShape} from "@bufbuild/protobuf";
import {timestampDate, type Timestamp} from "@bufbuild/protobuf/wkt";
import {safeNumber} from "../api/safeNumber";
import {expiresAfterFromName, expiresAfterName} from "../domain/ExpiresAfter";
import {TransferFileAdditionState} from "../domain/TransferFileAdditionState";
import {TransferLegalHoldState} from "../domain/TransferLegalHoldState";
import {TransferRecoveryState} from "../domain/TransferRecoveryState";
import {filePlaintextLength} from "../download/encrypted/filePlaintextLength";
import {
	AccountFile_Direction,
	AccountFile_Encryption,
	AccountFile_State,
	type AccountFile as AccountFileMessage,
	type ListAccountFilesResponse,
} from "../gen/kitecorp/transferify/transfer/v1/account_files_pb";
import type {UpdateTransferRequestSchema} from "../gen/kitecorp/transferify/transfer/v1/transfer_changes_pb";
import type {ListTransfersResponse} from "../gen/kitecorp/transferify/transfer/v1/transfer_list_pb";
import {
	ExpireAfter,
	TransferState_FileAdditionState,
	TransferState_LegalHoldState,
	TransferState_RecoveryState,
	type TransferFile as TransferFileMessage,
	type TransferState as TransferStateMessage,
	type TransferSummary,
} from "../gen/kitecorp/transferify/transfer/v1/transfer_pb";
import type {
	PatchTransferBody, TransferFile, TransferListEntry, TransferListPage, TransferState,
} from "./contacts/TransferApi";
import type {
	AccountFile, AccountFilesPage, FileDirection, FileEncryption, FileLinkState,
} from "./files/accountFiles";

/**
 * The Transfers page's messages (kitecorp/transferify-ui#4059) read into the app's own types, and the update the
 * other way. Pure functions of one value each.
 *
 * Times become ISO text with their milliseconds, because the Files tab orders files by their instant and keeps the
 * api's order on a tie: whole seconds would reorder files stored within one second. What a message leaves out reads
 * as the app's own none: an absent name or creation moment of a transfer's file is the empty text its type says
 * (`parseUtc("")` reads it as no date, as it read the REST `null`), an absent date elsewhere is null.
 */

const RECOVERY_STATES: Readonly<Partial<Record<TransferState_RecoveryState, TransferRecoveryState>>> = {
	[TransferState_RecoveryState.LIVE]: TransferRecoveryState.LIVE,
	[TransferState_RecoveryState.RECOVERABLE]: TransferRecoveryState.RECOVERABLE,
	[TransferState_RecoveryState.RECOVERED]: TransferRecoveryState.RECOVERED,
	[TransferState_RecoveryState.PURGED]: TransferRecoveryState.PURGED,
	[TransferState_RecoveryState.INELIGIBLE]: TransferRecoveryState.INELIGIBLE,
};

const FILE_ADDITION_STATES: Readonly<Partial<Record<TransferState_FileAdditionState, TransferFileAdditionState>>> = {
	[TransferState_FileAdditionState.SUPPORTED]: TransferFileAdditionState.SUPPORTED,
	[TransferState_FileAdditionState.UNSUPPORTED]: TransferFileAdditionState.UNSUPPORTED,
	[TransferState_FileAdditionState.INCOMPLETE]: TransferFileAdditionState.INCOMPLETE,
	[TransferState_FileAdditionState.EXPIRED]: TransferFileAdditionState.EXPIRED,
};

const LEGAL_HOLD_STATES: Readonly<Partial<Record<TransferState_LegalHoldState, TransferLegalHoldState>>> = {
	[TransferState_LegalHoldState.HELD]: TransferLegalHoldState.HELD,
	[TransferState_LegalHoldState.AVAILABLE]: TransferLegalHoldState.AVAILABLE,
	[TransferState_LegalHoldState.UNSUPPORTED]: TransferLegalHoldState.UNSUPPORTED,
	[TransferState_LegalHoldState.INCOMPLETE]: TransferLegalHoldState.INCOMPLETE,
	[TransferState_LegalHoldState.EXPIRED]: TransferLegalHoldState.EXPIRED,
};

/** An instant as ISO text with its milliseconds, or null when the message holds none. */
function instantOrNull(timestamp: Timestamp | undefined): string | null {
	return timestamp === undefined ? null : timestampDate(timestamp).toISOString();
}

/**
 * A transfer's state. A state this bundle cannot name reads as the one that offers nothing: no recovery, no added
 * files and no hold, where a guess could advertise an action the api would refuse.
 */
export function transferStateOf(message: TransferStateMessage): TransferState {
	return {
		id: message.id,
		encodedId: message.encodedId,
		message: message.message ?? null,
		keepOnline: message.keepOnline,
		// A legacy row recorded without a retention carries the unspecified value, which names no rung.
		expireAfter: expiresAfterFromName(ExpireAfter[message.expireAfter]),
		notifyOnDownload: message.notifyOnDownload,
		createdDate: instantOrNull(message.createdAt) ?? "",
		recipients: message.recipients,
		expiresAt: instantOrNull(message.expiresAt),
		recoveryState: RECOVERY_STATES[message.recoveryState] ?? TransferRecoveryState.INELIGIBLE,
		recoverableUntil: instantOrNull(message.recoverableUntil),
		fileAdditionState: FILE_ADDITION_STATES[message.fileAdditionState] ?? TransferFileAdditionState.UNSUPPORTED,
		complete: message.complete,
		suspended: message.suspended,
		legalHold: message.legalHold,
		legalHoldState: LEGAL_HOLD_STATES[message.legalHoldState] ?? TransferLegalHoldState.UNSUPPORTED,
		refusedDownloads: message.refusedDownloads,
		senderCompany: message.company === undefined ? null : {name: message.company, taxId: message.taxId ?? null},
	};
}

/** One row of the Sent list. A row without its state is not a transfer, so it is refused rather than invented. */
export function transferListEntryOf(summary: TransferSummary): TransferListEntry {
	if (summary.state === undefined) {
		throw new RangeError("a transfer row without its state");
	}
	return {
		...transferStateOf(summary.state),
		fileCount: summary.fileCount,
		totalBytes: safeNumber(summary.totalBytes),
		firstFileName: summary.firstFileName ?? null,
		recipientGroups: summary.state.recipientGroups,
		zeroKnowledge: summary.state.zeroKnowledge,
	};
}

export function transferListPageOf(response: ListTransfersResponse): TransferListPage {
	return {
		transfers: response.transfers.map(transferListEntryOf),
		totalTransfers: response.totalTransfers,
		totalFiles: safeNumber(response.totalFiles),
		totalBytes: safeNumber(response.totalBytes),
	};
}

/**
 * One of the owner's files, where `length` is the size of the file the sender picked. An encrypted file is stored as
 * ciphertext, a stream header plus a tag per chunk, which for a tiny file is most of what is stored, so it is sized
 * by the same inversion the recipient page uses ({@link filePlaintextLength}); a file stored unencrypted keeps its
 * size, each by its own flag (kitecorp/transferify-ui#2997).
 *
 * Only once its upload was initiated, though. The owner's list also holds files still uploading, and an added file
 * is marked encrypted the moment it is added while its length stays the size the sender declared until the
 * initiate records its framing, chunk size and ciphertext length together (the api's `File.commercialLength` reads
 * a row the same way). So a missing chunk size means the length is not ciphertext yet, and inverting it would show
 * a small file as empty.
 */
export function transferFileOf(file: TransferFileMessage): TransferFile {
	const stored = safeNumber(file.length);
	const length = file.encrypted && file.cipherChunkSize !== undefined
		? filePlaintextLength({encrypted: true, length: stored, cipherChunkSize: file.cipherChunkSize})
		: stored;
	return {
		id: file.id,
		name: file.filename ?? "",
		length,
		encrypted: file.encrypted,
		hash: file.hash ?? null,
		magnetURI: file.magnetUri ?? null,
		createdDate: instantOrNull(file.createdAt) ?? "",
		relativePath: file.relativePath ?? null,
	};
}

/**
 * An account file's enum as the text the Files tab reads. A value this bundle cannot name passes through as its
 * number in text, as the REST mapper passed whatever text the api sent, so the tab's own lookups decide what to
 * show for it.
 */
function enumText<T extends string>(names: Readonly<Record<number, string>>, value: number): T {
	return (names[value] ?? String(value)) as T;
}

export function accountFileOf(file: AccountFileMessage): AccountFile {
	return {
		id: file.id,
		name: file.filename ?? null,
		length: safeNumber(file.length),
		contentType: file.contentType ?? null,
		createdAt: instantOrNull(file.createdAt),
		relativePath: file.relativePath ?? null,
		direction: enumText<FileDirection>(AccountFile_Direction, file.direction),
		transferId: file.transferId,
		transferTitle: file.transferTitle ?? null,
		requestId: file.requestId ?? null,
		requestTitle: file.requestTitle ?? null,
		state: enumText<FileLinkState>(AccountFile_State, file.state),
		encryption: enumText<FileEncryption>(AccountFile_Encryption, file.encryption),
		peerToPeer: file.peerToPeer,
		recipients: file.recipients,
		recipientGroups: file.recipientGroups,
		uploaderEmail: file.uploaderEmail ?? null,
		uploaderName: file.uploaderName ?? null,
	};
}

export function accountFilesPageOf(response: ListAccountFilesResponse): AccountFilesPage {
	return {
		totalFiles: safeNumber(response.totalFiles),
		totalBytes: safeNumber(response.totalBytes),
		pageSize: response.pageSize,
		files: response.files.map(accountFileOf),
	};
}

/**
 * The update the owner's change asks for. A field is set only when the body holds it: an absent field leaves the
 * transfer as it is, and a present empty note clears it.
 */
export function updateTransferRequestOf(
	transferId: string,
	body: PatchTransferBody,
): MessageInitShape<typeof UpdateTransferRequestSchema> {
	return {
		transferId,
		...(body.expireAfter === undefined ? {} : {expireAfter: ExpireAfter[expiresAfterName(body.expireAfter)]}),
		...(body.message === undefined ? {} : {message: body.message}),
		...(body.addRecipients === undefined ? {} : {addRecipients: body.addRecipients}),
		...(body.revokeRecipients === undefined ? {} : {revokeRecipients: body.revokeRecipients}),
	};
}
